import { useTranslation } from '../../../../../shared/i18n';
import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Select, Space, Typography, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import domainRegistrationService from '../../../../services/domainRegistrationService';
import Money from '../../../../../shared/components/Money';
import { formatDate } from '../../../../../shared/utils/date';
import { userPath } from '../../../../../shared/config/paths';

const { Text } = Typography;

const YEAR_OPTIONS = [1, 2, 3, 5, 10].map((y) => ({ value: y, label: `${y} 年` }));

/**
 * 续费（单个/批量）：选年限 → 生成续费账单 → 跳转账单支付。
 * domains: 用户域名记录数组
 */
const RenewModal = ({ open, domains = [], onClose, onDone }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [years, setYears] = useState(1);
    const [prices, setPrices] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        setYears(1);
        // 续费按域名自己的注册商计价（与续费账单一致），所以按域名 id 取价
        domains.filter((d) => d.tld).forEach(async (d) => {
            try {
                const r = await domainRegistrationService.getTldPricing(d.tld, 1, { domain_id: d.id });
                const row = Array.isArray(r?.data) ? r.data[0] : null;
                if (row) setPrices((p) => ({ ...p, [d.id]: { renew: Number(row.renew_price), currency: row.currency } }));
            } catch (_) { /* price is informational only */ }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, domains.map((d) => d.id).join(',')]);

    const total = useMemo(() => {
        let sum = 0;
        let currency = null;
        for (const d of domains) {
            const p = prices[d.id];
            if (!p || !p.renew) return null;
            if (currency && p.currency !== currency) return null;
            currency = p.currency;
            sum += p.renew * years;
        }
        return currency ? { sum, currency } : null;
    }, [domains, prices, years]);

    const submit = async () => {
        setSubmitting(true);
        const invoiceIds = [];
        const failed = [];
        for (const d of domains) {
            try {
                const r = await domainRegistrationService.renewDomain({ id: d.id, years });
                if (r.success) {
                    if (r.data?.invoice_id) invoiceIds.push(r.data.invoice_id);
                } else {
                    failed.push(`${d.domain_unicode || d.domain}：${r.error || t('Failed')}`);
                }
            } catch (e) {
                failed.push(`${d.domain_unicode || d.domain}：${e.error || e.message || t('Failed')}`);
            }
        }
        setSubmitting(false);
        if (failed.length) message.error(failed.slice(0, 3).join('；'), 6);
        if (invoiceIds.length === 0) return;
        onDone && onDone();
        onClose && onClose();
        message.success(t('Renewal invoice created. Please complete payment'));
        if (invoiceIds.length === 1) {
            navigate(userPath(`/billing/invoices/${invoiceIds[0]}`));
        } else {
            navigate(userPath('/billing/invoices'));
        }
    };

    return (
        <Modal
            title={domains.length > 1 ? t('Bulk renewal ({{length}})', { length: domains.length }) : t('Renew domain')}
            open={open}
            onCancel={onClose}
            onOk={submit}
            okText={t('Pay now')}
            confirmLoading={submitting}
            destroyOnClose
        >
            <div style={{ maxHeight: 220, overflow: 'auto', marginBottom: 16 }}>
                {domains.map((d) => (
                    <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                        <Text>{d.domain_unicode || d.domain}</Text>
                        <Text type="secondary">{t('Expires {{date}}', { date: formatDate(d.expires_at) })}</Text>
                    </div>
                ))}
            </div>
            <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
                <Space>
                    <Text>{t('Renewal term (years)')}</Text>
                    <Select value={years} onChange={setYears} options={YEAR_OPTIONS.map(o => ({ ...o, label: t('{{y}} years', { y: o.value }) }))} style={{ width: 100 }} />
                </Space>
                {total && (
                    <Text strong style={{ fontSize: 16 }}>
                        {t('Approximately')} <Money amount={total.sum} currency={total.currency} />
                    </Text>
                )}
            </Space>
        </Modal>
    );
};

export default RenewModal;
